import axios from 'axios'
import type {
  CompanyListResponse,
  CompanyFilterParams,
  CompanyFilterOptions,
  Export,
  ExportFormat,
  ExportListResponse,
  HealthCheck,
  Job,
  JobActionResponse,
  JobCreateRequest,
  JobListResponse,
  LogsResponse,
  SettingsBundle,
} from '@/types'

type ApiErrorPayload = {
  error?: string
  message?: string
  detail?: string
}

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
})

export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as ApiErrorPayload | undefined
    return payload?.message || payload?.detail || error.message
  }
  if (error instanceof Error) return error.message
  return 'Не удалось выполнить запрос'
}

export const jobsApi = {
  list: async (page = 1, pageSize = 20, status?: string): Promise<JobListResponse> => {
    const { data } = await api.get<JobListResponse>('/jobs', { params: { page, page_size: pageSize, status } })
    return data
  },

  get: async (id: number): Promise<Job> => {
    const { data } = await api.get<Job>(`/jobs/${id}`)
    return data
  },

  create: async (payload: JobCreateRequest): Promise<Job> => {
    const { data } = await api.post<Job>('/jobs', payload)
    return data
  },

  start: async (id: number): Promise<JobActionResponse> => {
    const { data } = await api.post<JobActionResponse>(`/jobs/${id}/start`)
    return data
  },

  pause: async (id: number): Promise<JobActionResponse> => {
    const { data } = await api.post<JobActionResponse>(`/jobs/${id}/pause`)
    return data
  },

  resume: async (id: number): Promise<JobActionResponse> => {
    const { data } = await api.post<JobActionResponse>(`/jobs/${id}/resume`)
    return data
  },

  cancel: async (id: number): Promise<JobActionResponse> => {
    const { data } = await api.post<JobActionResponse>(`/jobs/${id}/cancel`)
    return data
  },

  getLogs: async (id: number, page = 1, pageSize = 50): Promise<LogsResponse> => {
    const { data } = await api.get<LogsResponse>(`/jobs/${id}/logs`, { params: { page, page_size: pageSize } })
    return data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/jobs/${id}`)
  },
}

export const companiesApi = {
  list: async (jobId: number, page = 1, pageSize = 50): Promise<CompanyListResponse> => {
    const { data } = await api.get<CompanyListResponse>(`/companies/job/${jobId}`, { params: { page, page_size: pageSize } })
    return data
  },

  search: async (jobId: number, query: string, page = 1, pageSize = 50): Promise<CompanyListResponse> => {
    const { data } = await api.get<CompanyListResponse>(`/companies/job/${jobId}/search`, { params: { query, page, page_size: pageSize } })
    return data
  },

  filter: async (jobId: number, filters: CompanyFilterParams, page = 1, pageSize = 50): Promise<CompanyListResponse> => {
    const params: Record<string, unknown> = { page, page_size: pageSize }
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value
      }
    })
    const { data } = await api.get<CompanyListResponse>(`/companies/job/${jobId}/filter`, { params })
    return data
  },

  getFilterOptions: async (jobId: number): Promise<CompanyFilterOptions> => {
    const { data } = await api.get<CompanyFilterOptions>(`/companies/job/${jobId}/filter-options`)
    return data
  },
}

export const exportApi = {
  create: async (jobId: number, format: ExportFormat, options?: { includeRaw?: boolean; includeErrors?: boolean; includeDuplicates?: boolean }): Promise<Export> => {
    const { data } = await api.post<Export>(`/export/job/${jobId}`, null, {
      params: { format, include_raw: options?.includeRaw ?? false, include_errors: options?.includeErrors ?? false, include_duplicates: options?.includeDuplicates ?? false },
    })
    return data
  },

  list: async (jobId: number): Promise<ExportListResponse> => {
    const { data } = await api.get<ExportListResponse>(`/export/job/${jobId}/list`)
    return data
  },

  downloadUrl: (exportId: number): string => `/api/v1/export/${exportId}/download`,
}

export const settingsApi = {
  list: async (): Promise<SettingsBundle> => {
    const { data } = await api.get<SettingsBundle>('/settings')
    return data
  },

  update: async (key: string, value: Record<string, unknown>, description?: string): Promise<void> => {
    await api.put(`/settings/${key}`, { value, description })
  },
}

export const healthApi = {
  check: async (): Promise<HealthCheck> => {
    const { data } = await api.get<HealthCheck>('/health')
    return data
  },
}
